import { logger } from '../utils/logger';
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
    errorInfo?: ErrorInfo;
}

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 * Logs error information and displays a fallback UI
 */
class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false
    };

    public static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        if (process.env.NODE_ENV === 'development') {
            logger.error('ErrorBoundary caught:', error, errorInfo);
        }

        if (process.env.NODE_ENV === 'production') {
            // Send to backend audit log (no token needed — uses cookie)
            fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/audit-logs/admin-actions`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'client_error',
                    entity_type: 'ErrorBoundary',
                    description: error.message,
                }),
            }).catch(() => { /* non-fatal */ });
        }

        this.setState({ errorInfo });
    }

    private handleReset = () => {
        this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    };

    public render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default fallback UI
            return (
                <div className="error-boundary-container" style={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2rem',
                    backgroundColor: 'var(--srs-bg)'
                }}>
                    <div className="error-boundary-content" style={{
                        maxWidth: '600px',
                        textAlign: 'center',
                        padding: '3rem',
                        backgroundColor: 'white',
                        borderRadius: 'var(--radius-lg, 20px)',
                        boxShadow: 'none'
                    }}>
                        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚠️</div>
                        <h2 style={{
                            color: 'var(--srs-indigo, var(--srs-primary))',
                            marginBottom: '1rem',
                            fontSize: '1.75rem'
                        }}>
                            Oops! Something went wrong
                        </h2>
                        <p style={{
                            color: 'var(--srs-muted)',
                            marginBottom: '2rem',
                            lineHeight: '1.6'
                        }}>
                            We're sorry for the inconvenience. An unexpected error has occurred.
                            Please try refreshing the page or contact support if the problem persists.
                        </p>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details style={{
                                marginBottom: '2rem',
                                textAlign: 'left',
                                padding: '1rem',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '8px',
                                fontSize: '0.875rem'
                            }}>
                                <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: '0.5rem' }}>
                                    Error Details (Development Only)
                                </summary>
                                <pre style={{
                                    overflow: 'auto',
                                    fontSize: '0.75rem',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                    {this.state.error.toString()}
                                    {this.state.errorInfo && '\n\n' + this.state.errorInfo.componentStack}
                                </pre>
                            </details>
                        )}

                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                            <button
                                onClick={() => window.location.reload()}
                                className="btn btn-primary"
                                style={{
                                    padding: '0.75rem 2rem',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    borderRadius: 'var(--radius-md, 16px)',
                                    border: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                Refresh Page
                            </button>
                            <button
                                onClick={this.handleReset}
                                className="btn btn-outline-primary"
                                style={{
                                    padding: '0.75rem 2rem',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    borderRadius: 'var(--radius-md, 16px)',
                                    cursor: 'pointer'
                                }}
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
